'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    addBlockedIp,
    BlockedIp,
    getBlockedIps,
    removeBlockedIp,
} from '@/lib/api/security/security-api'
import { useEffect, useState } from 'react'
import { MdAdd, MdBlock, MdDelete, MdPublic, MdRefresh } from 'react-icons/md'
import { toast } from 'react-toastify'

const formatDate = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleString('pt-BR') : '—'

export default function BlockedIpsPage() {
    const [items, setItems] = useState<BlockedIp[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleteTarget, setDeleteTarget] = useState<BlockedIp | null>(null)
    const [processing, setProcessing] = useState(false)

    const [form, setForm] = useState<{
        ipOrCidr: string
        reason: string
        scope: 'GLOBAL' | 'ORG'
        expiresAt: string
    }>({ ipOrCidr: '', reason: '', scope: 'GLOBAL', expiresAt: '' })

    const load = async () => {
        setLoading(true)
        try {
            const res = await getBlockedIps()
            if (res.success && res.result) setItems(res.result)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    const handleAdd = async () => {
        if (!form.ipOrCidr.trim()) {
            toast.warning('Informe o IP ou faixa CIDR')
            return
        }
        setSaving(true)
        try {
            const res = await addBlockedIp({
                ipOrCidr: form.ipOrCidr.trim(),
                reason: form.reason.trim() || undefined,
                scope: form.scope,
                expiresAt: form.expiresAt || undefined,
            })
            if (res.success) {
                toast.success('IP bloqueado')
                setForm({
                    ipOrCidr: '',
                    reason: '',
                    scope: 'GLOBAL',
                    expiresAt: '',
                })
                load()
            } else {
                toast.error(res.error || 'Erro ao bloquear IP')
            }
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteTarget) return
        setProcessing(true)
        try {
            const res = await removeBlockedIp(deleteTarget.id)
            if (res.success) {
                toast.success('Bloqueio removido')
                setDeleteTarget(null)
                load()
            } else toast.error(res.error || 'Erro ao remover')
        } finally {
            setProcessing(false)
        }
    }

    return (
        <div className="p-4 md:p-6 max-w-5xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <MdBlock /> Bloqueio de IP
                </h1>
                <p className="text-base-content/60 text-sm mt-1">
                    Bloqueie IPs ou faixas CIDR. Acessos via rede TOR são
                    bloqueados automaticamente (lista atualizada
                    periodicamente).
                </p>
            </div>

            {/* Form */}
            <div className="card bg-base-100 shadow mb-4">
                <div className="card-body p-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="form-control md:col-span-1">
                            <label className="label">
                                <span className="label-text">IP / CIDR</span>
                            </label>
                            <input
                                className="input input-bordered input-sm"
                                value={form.ipOrCidr}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        ipOrCidr: e.target.value,
                                    })
                                }
                                placeholder="1.2.3.4 ou 1.2.3.0/24"
                            />
                        </div>
                        <div className="form-control md:col-span-1">
                            <label className="label">
                                <span className="label-text">
                                    Motivo (opcional)
                                </span>
                            </label>
                            <input
                                className="input input-bordered input-sm"
                                value={form.reason}
                                onChange={e =>
                                    setForm({ ...form, reason: e.target.value })
                                }
                                placeholder="Ex: abuso, ataque..."
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Escopo</span>
                            </label>
                            <select
                                className="select select-bordered select-sm"
                                value={form.scope}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        scope: e.target.value as
                                            | 'GLOBAL'
                                            | 'ORG',
                                    })
                                }
                            >
                                <option value="GLOBAL">
                                    Global (plataforma)
                                </option>
                                <option value="ORG">Minha organização</option>
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">
                                    Expira em (opcional)
                                </span>
                            </label>
                            <input
                                type="datetime-local"
                                className="input input-bordered input-sm"
                                value={form.expiresAt}
                                onChange={e =>
                                    setForm({
                                        ...form,
                                        expiresAt: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <div className="flex justify-end mt-3">
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleAdd}
                            disabled={saving}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-xs" />
                            ) : (
                                <MdAdd />
                            )}
                            Bloquear IP
                        </button>
                    </div>
                </div>
            </div>

            {/* Lista */}
            <div className="card bg-base-100 shadow">
                <div className="card-body p-0">
                    <div className="flex items-center justify-between p-4">
                        <h2 className="font-semibold">IPs bloqueados</h2>
                        <button
                            className="btn btn-ghost btn-sm"
                            onClick={load}
                        >
                            <MdRefresh /> Atualizar
                        </button>
                    </div>
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <span className="loading loading-spinner loading-lg text-secondary" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-12 text-base-content/60">
                            Nenhum IP bloqueado manualmente.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>IP / CIDR</th>
                                        <th>Motivo</th>
                                        <th>Escopo</th>
                                        <th>Expira</th>
                                        <th>Criado</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(ip => (
                                        <tr key={ip.id}>
                                            <td className="font-mono text-sm">
                                                {ip.ipOrCidr}
                                            </td>
                                            <td className="text-sm">
                                                {ip.reason || '—'}
                                            </td>
                                            <td>
                                                {ip.organizationId == null ? (
                                                    <span className="badge badge-sm badge-warning gap-1">
                                                        <MdPublic className="text-xs" />
                                                        Global
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-sm badge-ghost">
                                                        Org #{ip.organizationId}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-xs text-base-content/60">
                                                {formatDate(ip.expiresAt)}
                                            </td>
                                            <td className="text-xs text-base-content/60">
                                                {formatDate(ip.createdAt)}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-ghost btn-xs text-error"
                                                    onClick={() =>
                                                        setDeleteTarget(ip)
                                                    }
                                                    title="Remover bloqueio"
                                                >
                                                    <MdDelete />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remover bloqueio"
                message={`Remover o bloqueio de ${deleteTarget?.ipOrCidr}?`}
                loading={processing}
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
